<template>
  <div v-if="listingStore.listingData && listingStore.listingData.hits" class="listing-search">
    <!-- Action Row -->
    <div class="__listing-search-action-raw">
      <ElementsInputsDropdown v-model="selectedSortBy" class="__sort-by-dropdown" label="Sort by" return-full-object :items="sortByItems" @change="setSortBy" />
      <div class="__view-selector-placeholder" />
    </div>

    <!-- Results Count -->
    <div class="__results-info-raw">
      <span>{{ listingResultStats }}</span>
    </div>

    <!-- Builder only warning -->
    <!--    <span v-if="builderName" class="__builder-listing-only-notice"><strong>Important!</strong> Showing only {{ builderName }}'s listings. Go back to <nuxt-link to="/listing/search">all listing search</nuxt-link>.</span>-->

    <!-- Listing Items -->
    <div v-if="listingStore.listingData.hits && listingStore.listingData.hits.length > 0" class="row justify-content-center __listing-items-wrapper">
      <div v-for="(hit, key) in formattedListingHits" :key="`listing-${key}`" :class="listingStore.viewType === 'Map' ? 'col-md-12 col-lg-6' : 'col-sm-6 col-md-6 col-lg-4 col-xl-3'" class="d-flex justify-content-center listing-widget-item-wrapper">
        <ListingWidget v-if="hit.document.category === 'Listing'" :listing="hit.document" :type="hit.document.listingTypes.includes('Design') ? 'design' : 'listing'" :is-loading="listingStore.isLoading" :analytics-event-data="{searchResultId: listingStore.requestId, position: getPosition(key), displayedLocation: 'listing search'}" />
        <AdWidget v-if="hit.document.category === 'Ad'" :listing="hit.document" :analytics-event-data="{displayedLocation: 'listing_search_widget'}" />
      </div>
    </div>
    <div v-else-if="listingStore.isLoading" class="row __listing-items-loading">
      <div v-for="(i, key) in 4" :key="`listing-${key}`" :class="listingStore.viewType === 'Map' ? 'col-md-12 col-lg-6' : 'col-sm-6 col-md-4 col-lg-3'">
        <ListingWidget :is-loading="true" />
      </div>
    </div>
    <div v-else-if="listingStore.listingData && listingStore.listingData.found === 0" class="__listing-items-no-results">
      No results found for your search query!
    </div>
  </div>
</template>

<script setup lang="ts">
import {PropType, Ref} from 'vue';
import {useListingStore} from '~/store/listing';
import ListingSearchQueryHandler from '~/composables/listingSearchQuery';

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const route = useRoute();
const selectedSortBy: Ref<{label: string; value: {field: string | null; type: 'asc' | 'desc' | null}}> = ref({label: 'Popular', value: {field: null, type: null}});
const listingStore = useListingStore();
const sortByItems: Ref<{label: string, value: {field: string | null; type: 'asc' | 'desc' | null}}[]> = ref([
  {label: 'Popular', value: {field: null, type: null}},
  {label: 'Name A - Z', value: {field: 'name', type: 'asc' as 'asc' | 'desc'}},
  {label: 'Name Z - A', value: {field: 'name', type: 'desc' as 'asc' | 'desc'}},
  {label: 'Price Low to High', value: {field: 'price', type: 'asc' as 'asc' | 'desc'}},
  {label: 'Price High to Low', value: {field: 'price', type: 'desc' as 'asc' | 'desc'}},
]);

// PROPS Definitions
//----------------------------------------------------------------------------------------------------------------------
const props = defineProps({
  builderName: {
    type: [String, null] as PropType<string | null>,
    default: null,
  },
});

setSortByFromQuery();


/**
 * Listing result stats
 */
const listingResultStats: ComputedRef<string> = computed(() => {
  if (listingStore && listingStore.listingData && listingStore.listingData.request_params) {
    const foundResults = listingStore.listingData.found > 500 ? '500+' : listingStore.listingData.found;
    const upToResults =listingStore.listingData.page * listingStore.listingData.request_params.per_page;
    if (foundResults === 0) return '0 Results';
    return `Page ${listingStore.listingData.page} (Showing ${((listingStore.listingData.page - 1) * listingStore.listingData.request_params.per_page) + 1} - ${upToResults > foundResults ? foundResults : upToResults } Listing of ${ foundResults } Results)`;
  }
  return '';
});

/**
 * Set sort by from query
 */
function setSortByFromQuery () {
  if (route.query.sortBy && route.query.sortBy.length > 0) {
    const sortBy = (route.query.sortBy as string).split(':');
    selectedSortBy.value = sortByItems.value.find((item) => item.value.field === sortBy[0] && item.value.type === sortBy[1]) || sortByItems.value[0];
  }
}

/**
 * Get the position of this listing in the search results
 */
function shuffleArray(array: any[]) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

const formattedListingHits: ComputedRef<any[]> = computed(() => {
  let ads = listingStore.listingData.hits.filter((hit) => hit.document.category === 'Ad');
  const listings = listingStore.listingData.hits.filter((hit) => hit.document.category === 'Listing');

  // Shuffle the ads array
  ads = shuffleArray(ads);

  const result = [];
  let adIndex = 0;

  for (let i = 0; i < listings.length; i++) {
    if (i === 2 && adIndex < ads.length) { // Insert first ad at the 3rd position (index 2)
      result.push(ads[adIndex]);
      adIndex++;
    } else if (i > 2 && (i - 2) % 5 === 0 && adIndex < ads.length) { // Insert subsequent ads every 5th position after the first ad
      result.push(ads[adIndex]);
      adIndex++;
    }
    result.push(listings[i]);
  }

  // If there are remaining ads and we want to add them at the end
  while (adIndex < ads.length) {
    result.push(ads[adIndex]);
    adIndex++;
  }

  return result;
});

/**
 * Set sort by
 */
function setSortBy () {
  if (selectedSortBy.value) {
    ListingSearchQueryHandler.set({
      sortBy: selectedSortBy.value.value && selectedSortBy.value.value.field ? `${selectedSortBy.value.value.field}:${selectedSortBy.value.value.type}` : 'priority:desc',
    });
  }
}

/**
 * Get the position of this listing in the search results
 * @param key
 */
function getPosition (key: number) {
  return (listingStore.listingData.page - 1) * listingStore.listingData.request_params.per_page + key + 1;
}
</script>

<style lang="scss">
.listing-search {
  padding: 30px;

  .listing-widget-item-wrapper {
    padding-bottom: 30px;
  }

  .__listing-search-action-raw {
    display: flex;

    // Action row dropdown
    .el-input-dropdown.__sort-by-dropdown {
      min-width: 100px;
      max-width: 180px;
      background: rgb(var(--background-light));
      box-shadow: 0 1px 12px -3px rgba(0, 0, 0, 0.15);
      border-radius: 8px;
      padding: 5px;
      min-height: unset;
      height: 40px;
      margin-right: 12px;

      .__dropdown-label {
        color: rgb(var(--text-lighter-1));
        margin-bottom: 0;
        font-weight: 400;
        padding: 0 12px;
        font-size: 14px;
      }

      .__input-dropdown-inner {
        min-height: unset;

        .__selection {
          color: rgb(var(--background-dark));
          font-size: 14px;
          margin-top: -2px;
        }

        .__inner-icon {
          position: absolute;
          right: 8px;
          top: 12px;
        }
      }

      .__dropdown-items-wrapper {
        left: 0;
        top: 38px;
      }
    }

    .__view-selector-placeholder {
      width: 480px;
    }
  }

  .__builder-listing-only-notice {
    display: block;
    width: 100%;
    text-align: center;
    padding: 0px 5px 20px;
    font-size: 14px;

    strong {
      font-weight: 500;
    }
  }

  .__results-info-raw {
    min-height: 12px;
    margin: 20px 0;
    text-align: right;
    font-size: 14px;
    color: rgb(var(--text-lighter-1));
  }

  .__listing-items-no-results {
    text-align: center;
    font-size: 14px;
    color: rgb(var(--text-lighter-1));
    margin-top: 40px;
  }
}
</style>
